import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { firstDefined } from '@gafe/common-ui';
import { filter, find, reduce } from 'lodash';

import { ProgressSpinner } from '@/components/generic/ProgressSpinner';
import { Modal, ModalBody, ModalHeader } from '@/components/proxy/reactstrap/Modal';
import { AbilityContext } from '@/hooks/ability/Can';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { ROUTES } from '@/router';
import { OrderDeliveryType, PriceBookEntryVm, ProductType, ProposalType, UserRole } from '@/types';

import { CancellationModal } from './components/CancellationModal';
import { Steps } from './components/Steps';
import {
  convertToOrderItem,
  getInitialState,
  hasTimberlineSolarItems,
  isAccessOpportunity,
  isDifferentTypeOfProduct,
  isInventoryTypeOfEntry,
  isProductServiceOrInventory,
  isRidgeCap,
  isStorageProduct,
  mapPriceBookItemToOrderItem,
} from './constants';
import { OrderModalContext, OrderModalDispatchContext, reducer } from './helpers';
import { OrderItemWrapper, Props, StepsName } from './types';

import styles from './SubmitOrderModal.module.scss';

export const SubmitOrderModal = (props: Props) => {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const { userInfo } = useAuth();
  const history = useHistory();
  const modalAnchorRef = useRef<HTMLDivElement>(null);
  const ability = useAbility(AbilityContext);
  const isBulkFulfillmentEnabled = ability.can('view', 'Bulk Fulfillment Program');
  const shouldShowEligibilityStep = props.proposalItems.some(hasTimberlineSolarItems);

  useEffect(() => {
    if (!props.modalConfig.isOpened) {
      return;
    }

    props.fetchOpportunity(props.modalConfig.opportunityId as string);
    props.fetchDesignToOrderProducts(props.modalConfig.opportunityId as string);
    props.fetchShippingAddresses();
  }, [props.modalConfig.isOpened]);

  useEffect(() => {
    if (props.opportunity?.syncedProposalId) {
      props.fetchProposalItems(props.opportunity.syncedProposalId);
    }
  }, [props.opportunity?.syncedProposalId]);

  useEffect(() => {
    if (props.opportunity) {
      const isPartialReRoofPreSelected =
        props.opportunity?.partialReRoof && state.order.partialReRoof === null;
      const watcherList = firstDefined([], props.opportunity.watcherList);

      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          opportunityId: props.opportunity?.id,
          bucketId: props.opportunity?.bucketId ?? null,
          partialReRoof: isPartialReRoofPreSelected ? true : null,
          shipmentContact: props.opportunity?.contact,
          deliveryType: OrderDeliveryType['Homeowner/installation address'],
          watcherList,
        },
      });
    }
  }, [props.opportunity]);

  useEffect(() => {
    if (state.order.opportunityId && props.allShippingAddresses.length > 0) {
      const shippingAddress =
        find(props.allShippingAddresses, sa => sa.isDefault) ?? props.allShippingAddresses[0];

      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          shipmentContact: shippingAddress,
          deliveryType: OrderDeliveryType['Company/warehouse address'],
        },
      });
    }
  }, [props.allShippingAddresses, props.allShippingAddresses.length, state.order.opportunityId]);

  useEffect(() => {
    if (props.opportunity) {
      let containsAsphaltShingle = false;
      let containsRidgeCap = false;
      let isRidgeCapRequired = false;
      const { proposals, syncedProposalId } = props.opportunity;
      const syncedProposal = find(proposals, p => p.id === syncedProposalId);
      const isTimberline = syncedProposal?.proposalType === ProposalType['Timberline Solar'];

      const orderItems = reduce(
        props.proposalItems,
        (result, pi) => {
          // do not add current proposal item to results when
          // its quantity is absent or set to 0
          if (pi.quantity === undefined || pi.quantity <= 0) {
            return result;
          }

          const pbe: PriceBookEntryVm | undefined = find(
            props.allPriceBookEntries,
            item =>
              item.productId === pi.product.id &&
              item.priceBookId === props.opportunity?.priceBookId,
          );

          if (!pbe || isStorageProduct(pbe)) {
            return result;
          }

          if (isProductServiceOrInventory(pbe)) {
            return [...result, convertToOrderItem(pbe, pi)];
          }

          if (pbe.product.type === ProductType['Asphalt Shingle']) {
            // do not add current pbe to results when it is not
            // a Timberline order or shingle is not available
            if (!isTimberline || !pbe.isProductAvailable) {
              return result;
            }

            // do not add current pbe to results when results
            // already contain a product of type asphalt shingle
            if (containsAsphaltShingle) {
              return result;
            }

            // asphalt shingle found
            containsAsphaltShingle = true;

            if (pbe.product.dependentProductId) {
              // asphalt shingle with ridge cap found
              isRidgeCapRequired = true;
            }
          } else if (pbe.product.type === ProductType['Ridge Cap']) {
            // do not add current pbe to results when it is not
            // a Timberline order
            if (!isTimberline) {
              return result;
            }

            // do not add current pbe to results when results
            // already contain a product of type ridge cap
            if (containsRidgeCap) {
              return result;
            }

            // ridge cap found
            containsRidgeCap = true;
          }

          return [...result, convertToOrderItem(pbe, pi)];
        },
        [] as OrderItemWrapper[],
      );

      // filter out ridge cap order items, if ridge cap is not required
      const primaryOrderItems = isRidgeCapRequired
        ? orderItems
        : filter(orderItems, oi => !isRidgeCap(oi));

      const restOrderItems = (() => {
        return props.allPriceBookEntries
          .filter(
            item => !primaryOrderItems.find(oi => oi.priceBookEntry.productId === item.productId),
          )
          .map(item => {
            if (
              !isDifferentTypeOfProduct(item, isAccessOpportunity(props.opportunity)) ||
              (!isBulkFulfillmentEnabled && isInventoryTypeOfEntry(item))
            ) {
              return null;
            }

            return {
              priceBookEntry: item,
              orderItem: mapPriceBookItemToOrderItem(item),
              proposalItem: null,
            };
          })
          .filter(item => item) as OrderItemWrapper[];
      })();

      // we display ridge cap section selection
      // for timberline orders with selected asphalt shingles
      const isRidgeCapSectionEnabled = isTimberline && isRidgeCapRequired;

      dispatch({
        type: 'UPDATE',
        payload: {
          visibleOrderItems: [...primaryOrderItems, ...restOrderItems],
          isRidgeCapSectionEnabled,
          adders: props.proposal ? [...props.proposal.adders] : [],
        },
      });
    }
  }, [
    ability,
    props.opportunity,
    props.allPriceBookEntries.length,
    props.proposalItems,
    props.allPriceBookEntries,
    isBulkFulfillmentEnabled,
    props.proposal,
  ]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE',
      payload: {
        isPriceHidden:
          isAccessOpportunity(props.opportunity) && userInfo?.role === UserRole['Sales'],
        isAccessProgram: isAccessOpportunity(props.opportunity),
        isDistribution: Boolean(props.opportunity?.isDistribution),
        proposalSolarProductType: props.proposal?.solarProduct,
      },
    });
  }, [ability, dispatch, props.opportunity?.accessProgram, userInfo?.role]);

  const closeSubmitOrderModal = useCallback((): void => {
    dispatch({
      type: 'INITIAL',
    });

    props.clearCreatedOrderId();
    props.clearCalculatedTax();
    props.clearOpportunity();
    props.closeSubmitOrderModal();
  }, [props]);

  const openCancellationModal = useCallback((): void => {
    const currentStep = state.wizardHeaderSteps.find(step => step.isActive === true);

    if (currentStep?.label === StepsName['Success']) {
      closeSubmitOrderModal();

      if (history.location.pathname.split('/dashboard/').length === 2) {
        history.push(ROUTES.Private.Dashboard.ActivityList);
      }

      return;
    }

    dispatch({
      type: 'UPDATE',
      payload: {
        isCancellationModalOpened: true,
      },
    });
  }, [closeSubmitOrderModal, history, state.wizardHeaderSteps]);

  if (
    !props.productLineName ||
    !props.homeownerAddress ||
    !props.proposal ||
    props.loadingModalDataInProgress
  ) {
    return (
      <Modal
        backdrop="static"
        className={styles.spinner_modal_dialog}
        fullscreen
        isOpen={props.modalConfig.isOpened}
        keyboard={false}
        modalClassName={styles.spinner_modal}
        toggle={openCancellationModal}
      >
        <ProgressSpinner />
      </Modal>
    );
  }

  return (
    <Modal
      backdrop="static"
      className={styles.modal_dialog}
      fullscreen
      isOpen={props.modalConfig.isOpened}
      keyboard={false}
      modalClassName={styles.modal}
      toggle={openCancellationModal}
    >
      {/* Created this anchor to avoid the not to the top scroll behavior when moving between steps */}
      <div className={styles.modal_top_anchor} ref={modalAnchorRef}>
        Anchor
      </div>
      <ModalHeader toggle={openCancellationModal}></ModalHeader>
      <ModalBody>
        <OrderModalContext.Provider value={state}>
          <OrderModalDispatchContext.Provider value={dispatch}>
            <div className={styles.order_title}>
              {state.isAccessProgram ? 'Request Contract' : 'Submit Order'}
            </div>
            <Steps
              closeSubmitOrderModal={closeSubmitOrderModal}
              openCancellationModal={openCancellationModal}
              shouldShowEligibilityStep={shouldShowEligibilityStep}
              titleRef={modalAnchorRef}
            />
            <CancellationModal submitModal={closeSubmitOrderModal} />
          </OrderModalDispatchContext.Provider>
        </OrderModalContext.Provider>
      </ModalBody>
    </Modal>
  );
};
